import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from "react";
import { ArrowRight, ChevronRight, Search, X } from "lucide-react";
import { Button } from "../../base/button/button";
import { Checkbox } from "../../base/checkbox/checkbox";
import { Input } from "../../base/input/input";
import { RadioGroup, RadioGroupItem } from "../../base/radio-group/radio-group";
import { Skeleton } from "../../base/skeleton/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../../base/select/select";
import { cn } from "../../../utils";
import { passwordManagerIgnoreProps } from "../../../constants/password-manager-ignore";
import { DialogPhasePanel } from "../resizable-dialog/DialogPhasePanel";
import { DialogShellNav } from "../dialog-shell/DialogShellNav";
import { DialogShellLayout } from "../dialog-shell/DialogShellLayout";
import { EntityPickerSuggestRow } from "./EntityPickerSuggestRow";
import { useEntityPickerState } from "./useEntityPickerState";
const DEPARTMENT_FILTER_ALL = "__all__";
const SKELETON_ROW_COUNT = 6;
function EntityPickerSkeletonList() {
    return /*#__PURE__*/ _jsx("div", {
        "aria-busy": "true",
        className: "flex flex-col gap-1",
        children: Array.from({
            length: SKELETON_ROW_COUNT
        }).map((_, i)=>/*#__PURE__*/ _jsxs("div", {
                "data-testid": "entity-picker-skeleton-row",
                className: "flex items-center gap-3 rounded-md px-3 py-2",
                children: [
                    /*#__PURE__*/ _jsx(Skeleton, {
                        className: "h-6 w-6 shrink-0 rounded-full"
                    }),
                    /*#__PURE__*/ _jsx(Skeleton, {
                        className: "h-4 flex-1"
                    })
                ]
            }, i))
    });
}
export const EntityPickerBody = /*#__PURE__*/ React.forwardRef(function EntityPickerBody(props, ref) {
    const { items, selectedIds, mode, config, suggestNew, excludeIds, onDraftChange, onViewModeChange, autofocusSearch = true, isLoading = false } = props;
    const state = useEntityPickerState({
        items,
        selectedIds,
        mode,
        config,
        excludeIds,
        onDraftChange,
        onViewModeChange
    });
    const { draft, setDraft, isSelected, toggle, query, setQuery, trimmedQuery, viewMode, setViewMode, bucketViewAvailable, activeFacet, setActiveFacet, byId, candidateItems, departmentFilteredItems, visibleItems, facetCounts, departmentCounts, results, allListSelected, allListRest, showBucketedEmptyView, showNoData, showEmpty, handleBackToBuckets } = state;
    const departmentFilter = config.departmentFilter;
    const selectedDepartmentOption = departmentFilter && departmentFilter.selectedId ? departmentFilter.options.find((o)=>o.id === departmentFilter.selectedId) : undefined;
    const breadcrumbSegments = selectedDepartmentOption?.pathLabels.length ? selectedDepartmentOption.pathLabels : selectedDepartmentOption ? [
        selectedDepartmentOption.label
    ] : [];
    React.useImperativeHandle(ref, ()=>({
            backToBuckets: ()=>{
                if (bucketViewAvailable) handleBackToBuckets();
            },
            getViewMode: ()=>viewMode,
            canReturnToBuckets: ()=>bucketViewAvailable
        }), [
        bucketViewAvailable,
        handleBackToBuckets,
        viewMode
    ]);
    const buildRowContext = React.useCallback((item, matches)=>{
        const meta = config.getRowMeta?.(item);
        const ctx = {
            matches
        };
        if (meta?.recommended) ctx.recommended = true;
        if (meta?.stats) ctx.stats = meta.stats;
        const matchInField = config.matchInField;
        if (matchInField && matches && matches.length > 0 && config.copy.matchInFieldTemplate) {
            const offField = matches.find((m)=>m.key && m.key !== matchInField.primaryField);
            if (offField?.key) {
                const label = matchInField.fieldLabels[offField.key] ?? offField.key;
                ctx.matchInFieldHint = config.copy.matchInFieldTemplate(label);
            }
        }
        return ctx;
    }, [
        config
    ]);
    const renderRowBody = (item, ctx)=>/*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: "flex-1 min-w-0",
                    children: config.renderRow(item, ctx)
                }),
                config.renderAllListTrailingTag && /*#__PURE__*/ _jsx("div", {
                    className: "shrink-0",
                    children: config.renderAllListTrailingTag(item)
                })
            ]
        });
    const renderMultiRow = (entry)=>{
        const id = entry.item._id;
        const checked = isSelected(id);
        const ctx = buildRowContext(entry.item, entry.matches);
        return /*#__PURE__*/ _jsxs("div", {
            role: "option",
            "aria-selected": checked,
            className: cn("flex items-center gap-3 rounded-md px-3 py-2 cursor-pointer hover:bg-accent", checked && "bg-primary/5"),
            onClick: ()=>toggle(id),
            children: [
                /*#__PURE__*/ _jsx(Checkbox, {
                    checked: checked,
                    onCheckedChange: ()=>toggle(id),
                    onClick: (e)=>e.stopPropagation()
                }),
                renderRowBody(entry.item, ctx)
            ]
        }, id);
    };
    const renderSingleRow = (entry)=>{
        const id = entry.item._id;
        const checked = draft[0] === id;
        const ctx = buildRowContext(entry.item, entry.matches);
        return /*#__PURE__*/ _jsxs("label", {
            role: "option",
            "aria-selected": checked,
            className: cn("flex items-center gap-3 rounded-md px-3 py-2 cursor-pointer hover:bg-accent", checked && "bg-primary/5"),
            children: [
                /*#__PURE__*/ _jsx(RadioGroupItem, {
                    value: id
                }),
                renderRowBody(entry.item, ctx)
            ]
        }, id);
    };
    const renderGroupHeader = (label)=>/*#__PURE__*/ _jsx("div", {
            role: "presentation",
            className: "sticky top-0 z-10 bg-background px-3 pt-2 pb-1 text-2xs font-semibold uppercase tracking-wider text-muted-foreground",
            children: label
        }, `__group-${label}`);
    const showAllListGroupHeaders = allListSelected.length > 0 && !!config.copy.allGroupSelectedLabel;
    const showSuggestRow = !!suggestNew && results.length > 0 && !!config.copy.suggestRowTitle;
    // Each empty state is labelled independently: a picker that can only act on
    // what was typed (e.g. "use this name as typed") supplies the with-query
    // label and omits the blank one, and then gets no button to press when there
    // is no query to act on.
    const emptyStateCtaLabel = trimmedQuery.length > 0 && config.copy.emptyStateCtaWithQuery ? config.copy.emptyStateCtaWithQuery(trimmedQuery) : config.copy.emptyStateCtaBlank;
    const showEmptyStateCta = !!suggestNew && results.length === 0 && !!emptyStateCtaLabel;
    const renderSuggestRow = ()=>suggestNew && config.copy.suggestRowTitle ? /*#__PURE__*/ _jsx(EntityPickerSuggestRow, {
            className: "mt-2",
            title: config.copy.suggestRowTitle,
            meta: config.copy.suggestRowMeta,
            onClick: ()=>suggestNew.onTrigger(trimmedQuery)
        }, "__suggest-row") : null;
    const facetLayout = config.facet?.layout ?? "rail";
    const departmentFilterLayout = config.departmentFilter?.layout ?? "select";
    const useFacetRail = !!config.facet && !!facetCounts && facetLayout === "rail";
    // Department rail kicks in only when the picker has *no* facet rail —
    // a single sidebar is enough; we don't try to stack two rails.
    const useDepartmentRail = !useFacetRail && !!departmentFilter && !!departmentCounts && departmentFilterLayout === "rail";
    const useSidebarRail = useFacetRail || useDepartmentRail;
    const showFacetChipsRow = viewMode === "all" && !useSidebarRail && (departmentFilter || config.facet && facetCounts);
    const renderChipBar = ()=>mode === "multi" && draft.length > 0 && config.getChipName && (viewMode === "bucket" || !bucketViewAvailable) ? /*#__PURE__*/ _jsxs("div", {
            className: "flex flex-wrap items-center gap-x-3 gap-y-1.5",
            children: [
                config.copy.selectedSectionLabel && /*#__PURE__*/ _jsx("p", {
                    className: "text-2xs font-semibold uppercase tracking-wider text-muted-foreground shrink-0",
                    children: config.copy.selectedSectionLabel(draft.length)
                }),
                draft.map((id)=>{
                    const item = byId.get(id);
                    if (!item) return null;
                    const name = config.getChipName(item);
                    if (!name) return null;
                    return /*#__PURE__*/ _jsxs("button", {
                        type: "button",
                        onClick: ()=>toggle(id),
                        "aria-label": config.copy.removeChipAriaLabel ? config.copy.removeChipAriaLabel(name) : undefined,
                        className: "inline-flex items-center gap-1.5 rounded-full bg-muted/60 hover:bg-muted px-2.5 py-1 text-xs text-foreground transition-colors",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "truncate max-w-[200px]",
                                children: name
                            }),
                            /*#__PURE__*/ _jsx(X, {
                                className: "h-3 w-3 text-muted-foreground"
                            })
                        ]
                    }, id);
                })
            ]
        }) : null;
    const renderDepartmentFilter = ()=>departmentFilter ? /*#__PURE__*/ _jsxs(Select, {
            value: departmentFilter.selectedId ?? DEPARTMENT_FILTER_ALL,
            onValueChange: (v)=>departmentFilter.onChange(v === DEPARTMENT_FILTER_ALL ? null : v),
            children: [
                /*#__PURE__*/ _jsx(SelectTrigger, {
                    className: "h-8 flex-shrink-0 rounded-full bg-muted/50 hover:bg-muted px-3 gap-1.5 border-0 text-sm",
                    "aria-label": config.copy.departmentFilterAriaLabel,
                    children: selectedDepartmentOption ? /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-1 min-w-0 flex-1 overflow-hidden",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-muted-foreground shrink-0",
                                children: departmentFilter.prefixLabel
                            }),
                            breadcrumbSegments.map((segment, idx)=>{
                                const isLast = idx === breadcrumbSegments.length - 1;
                                return /*#__PURE__*/ _jsxs(React.Fragment, {
                                    children: [
                                        idx > 0 && /*#__PURE__*/ _jsx(ChevronRight, {
                                            className: "h-3 w-3 text-muted-foreground shrink-0"
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            className: isLast ? "truncate" : "shrink-0",
                                            children: segment
                                        })
                                    ]
                                }, idx);
                            })
                        ]
                    }) : /*#__PURE__*/ _jsx("span", {
                        children: departmentFilter.allLabel
                    })
                }),
                /*#__PURE__*/ _jsxs(SelectContent, {
                    children: [
                        /*#__PURE__*/ _jsx(SelectItem, {
                            value: DEPARTMENT_FILTER_ALL,
                            children: departmentFilter.allLabel
                        }),
                        departmentFilter.options.map((opt)=>/*#__PURE__*/ _jsx(SelectItem, {
                                value: opt.id,
                                children: /*#__PURE__*/ _jsx("span", {
                                    style: {
                                        paddingLeft: `${opt.depth * 12}px`
                                    },
                                    children: opt.label
                                })
                            }, opt.id))
                    ]
                })
            ]
        }) : null;
    const renderFacetChips = ()=>config.facet && facetCounts ? /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx("button", {
                    type: "button",
                    onClick: ()=>setActiveFacet(null),
                    className: cn("flex-shrink-0 px-3 py-1 rounded-full text-sm border", activeFacet === null && "bg-primary text-primary-foreground"),
                    children: `${config.facet.allLabel} (${departmentFilteredItems.length})`
                }),
                facetCounts.map(([value, count])=>/*#__PURE__*/ _jsx("button", {
                        type: "button",
                        onClick: ()=>setActiveFacet(value),
                        className: cn("flex-shrink-0 px-3 py-1 rounded-full text-sm border", activeFacet === value && "bg-primary text-primary-foreground"),
                        children: `${value} (${count})`
                    }, value))
            ]
        }) : null;
    const renderFacetRail = ()=>{
        if (!config.facet || !facetCounts) return null;
        const facetSection = {
            id: "facet",
            heading: config.facet.allLabel,
            items: [
                {
                    id: "__all__",
                    label: `${config.facet.allLabel} (${departmentFilteredItems.length})`,
                    active: activeFacet === null,
                    onSelect: ()=>setActiveFacet(null)
                },
                ...facetCounts.map(([value, count])=>({
                        id: value,
                        label: `${value} (${count})`,
                        active: activeFacet === value,
                        onSelect: ()=>setActiveFacet(value)
                    }))
            ]
        };
        return /*#__PURE__*/ _jsx(DialogShellNav, {
            activeStyle: "flat",
            className: "h-full overflow-y-auto",
            sections: [
                facetSection
            ]
        });
    };
    const renderDepartmentRail = ()=>{
        if (!departmentFilter || !departmentCounts) return null;
        const departmentSection = {
            id: "department",
            heading: departmentFilter.prefixLabel,
            items: [
                {
                    id: "__all__",
                    label: `${departmentFilter.allLabel} (${candidateItems.length})`,
                    active: departmentFilter.selectedId === null,
                    onSelect: ()=>departmentFilter.onChange(null)
                },
                ...departmentFilter.options.map((opt)=>({
                        id: opt.id,
                        label: `${opt.label} (${departmentCounts.get(opt.id) ?? 0})`,
                        active: departmentFilter.selectedId === opt.id,
                        onSelect: ()=>departmentFilter.onChange(opt.id),
                        indent: opt.depth * 12
                    }))
            ]
        };
        return /*#__PURE__*/ _jsx(DialogShellNav, {
            activeStyle: "flat",
            className: "h-full overflow-y-auto",
            sections: [
                departmentSection
            ]
        });
    };
    const renderResultsList = ()=>{
        if (showNoData) {
            return /*#__PURE__*/ _jsxs("div", {
                className: "py-10 text-center",
                "data-testid": "entity-picker-no-data",
                children: [
                    config.headerIcon && /*#__PURE__*/ _jsx("div", {
                        className: "mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-md border bg-muted/30",
                        children: config.headerIcon
                    }),
                    config.copy.noDataTitle && /*#__PURE__*/ _jsx("p", {
                        className: "text-sm font-medium text-foreground",
                        children: config.copy.noDataTitle
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground mt-1",
                        children: config.copy.noDataMessage ?? config.copy.emptyMessage
                    }),
                    suggestNew && config.copy.emptyStateCtaBlank && /*#__PURE__*/ _jsx(Button, {
                        type: "button",
                        className: "mt-4",
                        onClick: ()=>suggestNew.onTrigger(""),
                        children: config.copy.emptyStateCtaBlank
                    })
                ]
            });
        }
        if (showBucketedEmptyView) {
            return config.bucketing.isLoading ? /*#__PURE__*/ _jsx(EntityPickerSkeletonList, {}) : config.bucketing.renderEmptyQueryView({
                items: visibleItems,
                selectedIds: draft,
                toggleId: toggle
            });
        }
        if (showEmpty && showEmptyStateCta && suggestNew) {
            return /*#__PURE__*/ _jsxs("div", {
                className: "py-10 text-center",
                children: [
                    config.headerIcon && /*#__PURE__*/ _jsx("div", {
                        className: "mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-md border bg-muted/30",
                        children: config.headerIcon
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: config.copy.emptyMessage
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        type: "button",
                        className: "mt-4",
                        onClick: ()=>suggestNew.onTrigger(trimmedQuery),
                        children: emptyStateCtaLabel
                    })
                ]
            });
        }
        // Consumer-supplied extra rows (e.g. pending-suggestion ghost rows, or one
        // "suggest new" row per kind). They sit at the top of the list so they
        // surface alongside entity matches without competing with the
        // pinned/selected group for primary position. A picker with no single-kind
        // `suggestNew` has no empty-state CTA either, so its pre-rows are also what
        // the zero-result state below shows.
        const preRows = config.renderActiveQueryPreRows?.({
            query: trimmedQuery
        });
        if (showEmpty) {
            return /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    preRows,
                    /*#__PURE__*/ _jsx("div", {
                        className: "py-8 text-center text-sm text-muted-foreground",
                        children: config.copy.emptyMessage
                    })
                ]
            });
        }
        // Flat children array keeps key identity when rows move between
        // the pinned and rest groups (single reconciler pass over rows).
        const renderRow = mode === "multi" ? renderMultiRow : renderSingleRow;
        const rows = [];
        if (preRows) rows.push(preRows);
        if (showAllListGroupHeaders && config.copy.allGroupSelectedLabel) {
            rows.push(renderGroupHeader(config.copy.allGroupSelectedLabel(allListSelected.length)));
        }
        for (const entry of allListSelected)rows.push(renderRow(entry));
        if (showAllListGroupHeaders && allListRest.length > 0 && config.copy.allGroupRestLabel) {
            rows.push(renderGroupHeader(config.copy.allGroupRestLabel(allListRest.length)));
        }
        for (const entry of allListRest)rows.push(renderRow(entry));
        if (showSuggestRow) {
            const row = renderSuggestRow();
            if (row) rows.push(row);
        }
        if (mode === "single") {
            return /*#__PURE__*/ _jsx(RadioGroup, {
                value: draft[0] ?? "",
                onValueChange: (v)=>setDraft([
                        v
                    ]),
                className: "flex flex-col gap-1",
                children: rows
            });
        }
        return /*#__PURE__*/ _jsx("div", {
            className: "flex flex-col gap-1",
            children: rows
        });
    };
    const renderShowAllCta = ()=>{
        if (viewMode !== "bucket" || !config.bucketing) return null;
        if (showNoData) return null;
        const switchToAllLabel = config.copy.switchToAllLabel;
        if (!config.copy.showAllCtaTitle && !switchToAllLabel) return null;
        return config.copy.showAllCtaTitle ? /*#__PURE__*/ _jsxs("button", {
            type: "button",
            onClick: ()=>setViewMode("all"),
            className: "flex w-full items-center gap-3 rounded-md border bg-muted/30 px-3 py-3 text-left transition-colors hover:bg-accent",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: "flex h-9 w-9 items-center justify-center rounded-md border bg-background shrink-0",
                    children: /*#__PURE__*/ _jsx(Search, {
                        className: "h-4 w-4"
                    })
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex-1 min-w-0",
                    children: [
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-sm font-medium",
                            children: config.copy.showAllCtaTitle
                        }),
                        config.copy.showAllCtaMeta && /*#__PURE__*/ _jsx("p", {
                            className: "text-xs text-muted-foreground",
                            children: config.copy.showAllCtaMeta(visibleItems.length)
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(ArrowRight, {
                    className: "h-4 w-4 text-muted-foreground shrink-0"
                })
            ]
        }) : /*#__PURE__*/ _jsxs(Button, {
            type: "button",
            variant: "ghost",
            size: "small",
            onClick: ()=>setViewMode("all"),
            className: "w-full justify-between",
            children: [
                switchToAllLabel?.(visibleItems.length),
                /*#__PURE__*/ _jsx(ArrowRight, {
                    className: "h-4 w-4"
                })
            ]
        });
    };
    // The chip bar lives outside the phase panel so it persists across
    // bucket↔all transitions.
    const bucketPhase = /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col flex-1 min-h-0 gap-3",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "flex-1 min-h-0 overflow-y-auto",
                children: renderResultsList()
            }),
            renderShowAllCta()
        ]
    });
    // The "back to suggestions" affordance lives in the modal footer
    // (EntityPickerModal / SuccessorEntityPickerModal), not inside the body.
    const allPhase = /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col flex-1 min-h-0 gap-3",
        children: [
            showFacetChipsRow && /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2 overflow-x-auto whitespace-nowrap",
                children: [
                    renderDepartmentFilter(),
                    renderFacetChips()
                ]
            }),
            useSidebarRail ? /*#__PURE__*/ _jsx(DialogShellLayout, {
                resizable: true,
                resizableAutoSaveId: "entity-picker-rail",
                sidebar: useFacetRail ? renderFacetRail() : renderDepartmentRail(),
                sidebarDefaultSize: 25,
                sidebarMinSize: 15,
                sidebarMaxSize: 50,
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-col h-full min-h-0 gap-2",
                    children: [
                        config.copy.resultCountLabel && /*#__PURE__*/ _jsx("div", {
                            className: "flex justify-end text-xs text-muted-foreground",
                            children: config.copy.resultCountLabel(results.length)
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "flex-1 min-h-0 overflow-y-auto",
                            children: renderResultsList()
                        })
                    ]
                })
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    config.copy.resultCountLabel && /*#__PURE__*/ _jsx("div", {
                        className: "flex justify-end text-xs text-muted-foreground -mt-1 mb-1",
                        children: config.copy.resultCountLabel(results.length)
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex-1 min-h-0 overflow-y-auto",
                        children: renderResultsList()
                    })
                ]
            })
        ]
    });
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col flex-1 min-h-0 gap-3",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: cn("flex items-center gap-2 border border-input bg-background rounded-md px-3 h-9 transition-colors", "hover:border-ring/50", "focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:border-ring"),
                children: [
                    /*#__PURE__*/ _jsx(Search, {
                        className: "w-4 h-4 text-muted-foreground shrink-0"
                    }),
                    /*#__PURE__*/ _jsx(Input, {
                        autoFocus: autofocusSearch,
                        placeholder: config.copy.searchPlaceholder,
                        value: query,
                        onChange: (e)=>{
                            const next = e.target.value;
                            setQuery(next);
                            if (next.trim().length > 0) setViewMode("all");
                        },
                        // This is an entity search field, never a credential field — the
                        // "E-Mail" hint in the placeholder otherwise makes browsers and
                        // password managers offer saved address/password autofill on focus.
                        type: "text",
                        name: "entity-picker-search",
                        autoComplete: "off",
                        spellCheck: false,
                        ...passwordManagerIgnoreProps,
                        className: "h-full bg-transparent border-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-0 shadow-none"
                    })
                ]
            }),
            isLoading ? /*#__PURE__*/ _jsx(EntityPickerSkeletonList, {}) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    renderChipBar(),
                    /*#__PURE__*/ _jsx(DialogPhasePanel, {
                        phaseKey: viewMode,
                        children: viewMode === "bucket" ? bucketPhase : allPhase
                    })
                ]
            })
        ]
    });
});

//# sourceMappingURL=EntityPickerBody.js.map